import { useEffect } from 'react';

import { routes } from 'Deer/constants';
import { L3_PATHS, staticRoutes } from 'Deer/constants/routes';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import { useParsedCohortQueryParam } from 'components/hooks/useParsedCohortQueryParam';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { getRelationToLoggedInEmployeeApi } from 'screens/oneOnOneRabbit/InstancePage/apis';
import { selectEnabledFeatureFlags } from 'selectors/enabledFeatureFlags';
import { selectIsAuthenticated } from 'selectors/loggedInEmployee';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { deerRabbitRoutesMap } from './deerRabbitRoutesMap';

const RABBIT_SETTINGS_MODULE_MAP = {
  goal: L3_PATHS.company.goal.settings,
  status: L3_PATHS.company.review.settings,
  oneOnOne: L3_PATHS.company.oneOnOne.settings,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getCohortIdFromSearch = (queryParams: any) => {
  // this is copypasta from src/components/hooks/useParsedCohortQueryParam.js
  let pC = { id: null };
  if (queryParams.cohort) {
    try {
      pC = JSON.parse(queryParams.cohort);
    } catch (err) {
      console.error('error in parsing cohort', err);
    }
  }
  return pC;
};

const getEmployeeRelation = async (employeeId: string) => {
  let relation = null;
  try {
    relation = await getRelationToLoggedInEmployeeApi({
      employeeId,
    });
  } catch (error) {
    console.error('Error in fetching relation to logged in employee. Redirecting normally', error);
  }
  return relation;
};

// Function to replace parameters in any route dynamically
const replaceParamsInRoute = (routeTemplate: string, pathParams: Record<string, string | number>): string => {
  let route = routeTemplate;

  // Replace each parameter in the route template with the corresponding value
  Object.keys(pathParams).forEach((paramName) => {
    const paramValue = pathParams[paramName];
    route = route.replace(`:${paramName}`, String(paramValue));
  });

  return route;
};

const redirectionLogger = ({
  deerRoute,
  rabbitRoute,
  routeType,
  redirectionType,
}: {
  deerRoute: string;
  rabbitRoute: string;
  routeType: 'LANDING_PAGE' | 'MODULE_SETTINGS' | 'PLATFORM_SETTINGS' | 'CUSTOM_PARAMS';
  redirectionType: 'deerRoute -> rabbitRoute' | 'rabbitRoute -> deerRoute';
}) => {
  const isDev = process.env.NODE_ENV === 'development' && process.env.PUBLIC_APP_ENABLE_REDIRECTION_LOGGER === 'true';

  if (isDev) {
    console.log('\x1b[34m%s\x1b[0m', 'Redirection Log:');
    console.log('\x1b[36m%s\x1b[0m', 'deerRoute:', deerRoute);
    console.log('\x1b[35m%s\x1b[0m', 'rabbitRoute:', rabbitRoute);
    console.log('\x1b[33m%s\x1b[0m', 'routeType:', routeType);
    console.log('\x1b[32m%s\x1b[0m', 'redirectionType:', redirectionType);
    console.log('\x1b[34m%s\x1b[0m', '----------------------');
  }
};

export type RedirectConfig = {
  deerRoute: string;
  rabbitRoute: string;
  routeType: 'LANDING_PAGE' | 'MODULE_SETTINGS' | 'PLATFORM_SETTINGS' | 'CUSTOM_PARAMS';
};

const useDeerRabbitRedirection = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const history = useHistory();
  const location = useLocation();
  const isNewNavEnabled = checkNewNavEnabled();
  const { user } = useAuthenticatedUser();

  const enabledFeatures = useSelector(selectEnabledFeatureFlags);

  const featureFlagsLoaded = enabledFeatures !== null;

  const queryParams = useParsedCohortQueryParam();

  const { id: cohortId = null } = queryParams ?? {};

  const isUserLoggedIn = !!user;

  const redirectConfigs = deerRabbitRoutesMap;

  useEffect(() => {
    // defer redirection until the user is authenticated and feature flags are loaded
    if (!isAuthenticated) {
      return;
    }
    const handleRedirection = () => {
      redirectConfigs.forEach(async ({ deerRoute, rabbitRoute, routeType }) => {
        const { id: cohortIdFromRabbitRoute } = getCohortIdFromSearch(queryString.parse(rabbitRoute.split('?')[1]));

        if (location.pathname === deerRoute && !isNewNavEnabled) {
          redirectionLogger({
            deerRoute,
            rabbitRoute,
            routeType,
            redirectionType: 'deerRoute -> rabbitRoute',
          });
          // all the deer -> rabbit re-directions are handled here irrespective of the routeType
          history.replace(rabbitRoute);
        } else if (
          location.pathname === rabbitRoute.split('?')[0] &&
          cohortId === cohortIdFromRabbitRoute &&
          isNewNavEnabled &&
          routeType === 'LANDING_PAGE'
        ) {
          redirectionLogger({
            deerRoute,
            rabbitRoute,
            routeType,
            redirectionType: 'rabbitRoute -> deerRoute',
          });
          const isOneOnOnePage = location.pathname.includes('oneOnOne');

          const otherEmployeeId = new URLSearchParams(location.search).get('otherEmpId');

          let redirectRoute = deerRoute;
          if (otherEmployeeId && isOneOnOnePage) {
            const relation = await getEmployeeRelation(otherEmployeeId);

            if (relation?.entity === 'REPORTEE' && deerRoute === L3_PATHS['my-data'].oneOnOne.myOneOnOne) {
              redirectRoute = L3_PATHS.team.directReports.oneOnOne.manage;
            } else if (relation?.entity === 'SKIP_REPORTEE' && deerRoute === L3_PATHS['my-data'].oneOnOne.myOneOnOne) {
              redirectRoute = L3_PATHS.team.allReports.oneOnOne.manage;
            }
          }

          // if is one on one audit page then pass the query param
          if (location.pathname.includes('oneOnOne/audit')) {
            const oneOnOneQueryParams = new URLSearchParams(location.search);
            // only get oneOnOneType query param
            const oneOnOneType = oneOnOneQueryParams.get('oneOnOneType');

            const newQueryParams = new URLSearchParams();
            newQueryParams.append('oneOnOneType', oneOnOneType ?? 'ACTIVE');

            history.replace({
              pathname: redirectRoute,
              search: newQueryParams.toString(),
            });
            return;
          }

          history.replace(redirectRoute);
        } else if (
          routeType === 'MODULE_SETTINGS' &&
          location.pathname === rabbitRoute.split('?')[0] &&
          isNewNavEnabled
        ) {
          const { module, activeTabId } = queryString.parse(location.search) ?? {};
          if (module === 'oneOnOne' && activeTabId === 'calendar') {
            const redirectRoute = staticRoutes['my-data']['my-settings'].calendar;
            redirectionLogger({
              deerRoute: redirectRoute,
              rabbitRoute,
              routeType,
              redirectionType: 'rabbitRoute -> deerRoute',
            });
            history.replace(redirectRoute);
            return;
          }
          const redirectRoute = `${RABBIT_SETTINGS_MODULE_MAP[module as string]}?activeTabId=${activeTabId}`;
          redirectionLogger({
            deerRoute: redirectRoute,
            rabbitRoute,
            routeType,
            redirectionType: 'rabbitRoute -> deerRoute',
          });
          history.replace(redirectRoute);
        } else if (routeType === 'PLATFORM_SETTINGS' && location.pathname === rabbitRoute && isNewNavEnabled) {
          redirectionLogger({
            deerRoute,
            rabbitRoute,
            routeType,
            redirectionType: 'rabbitRoute -> deerRoute',
          });
          history.replace(deerRoute);
        } else if (routeType === 'CUSTOM_PARAMS' && isNewNavEnabled) {
          const isQueryParamsRoute = rabbitRoute.includes('?');

          const isOneOneAuditPage = location.pathname.includes('oneOnOne/audit');
          const isOneOnOnePage = location.pathname.includes('oneOnOne');

          const otherEmployeeId = new URLSearchParams(location.search).get('otherEmpId');

          // Don't want "oneOnOne/:masterId" to match with "oneOnOne/audit"
          if (isOneOneAuditPage) {
            return;
          }

          if (isQueryParamsRoute && location.pathname === rabbitRoute.split('?')[0]) {
            const customQueryParams = queryString.parse(location.search);

            if (Object.keys(customQueryParams).length === 0) return;

            const redirectRoute = `${deerRoute}?${queryString.stringify(customQueryParams)}`;
            redirectionLogger({
              deerRoute: redirectRoute,
              rabbitRoute,
              routeType,
              redirectionType: 'rabbitRoute -> deerRoute',
            });
            history.replace(redirectRoute);
          } else {
            const { params = {} } =
              matchPath(location.pathname, {
                path: rabbitRoute,
                exact: true,
                strict: false,
              }) ?? {};
            if (Object.keys(params).length === 0) return;
            let redirectRoute = replaceParamsInRoute(deerRoute, params);
            const customQueryParams = queryString.parse(location.search);
            if (Object.keys(customQueryParams).length > 0 && isOneOnOnePage) {
              redirectRoute = `${redirectRoute}?${queryString.stringify(customQueryParams)}`;
            }
            if (otherEmployeeId && isOneOnOnePage) {
              const relation = await getEmployeeRelation(otherEmployeeId);

              if (relation?.entity === 'REPORTEE') {
                redirectRoute = redirectRoute.replace(routes.myData.index, routes.team.directReports);
              } else if (relation?.entity === 'SKIP_REPORTEE') {
                redirectRoute = redirectRoute.replace(routes.myData.index, routes.team.allReports);
              }
            }
            redirectionLogger({
              deerRoute: redirectRoute,
              rabbitRoute,
              routeType,
              redirectionType: 'rabbitRoute -> deerRoute',
            });
            history.replace(redirectRoute);
          }
        }
      });
    };

    if (isUserLoggedIn && featureFlagsLoaded) {
      handleRedirection();
    }
  }, [history, redirectConfigs, isNewNavEnabled, location, isUserLoggedIn, cohortId, user, featureFlagsLoaded]);
};

export { useDeerRabbitRedirection };
